export type ChangelogBlock =
  | { kind: 'heading'; text: string }
  | { kind: 'list'; items: string[] }
  | { kind: 'para'; text: string }

/** A domain and its version, parsed from the `Версии:` line of an entry. */
export interface DomainVersion {
  domain: string
  version: string
}

export interface ChangelogEntry {
  date: string
  /**
   * Domain versions shipped in this entry, parsed from the `Версии:` line.
   * Undefined when the entry has no such line. The changelog is keyed by date,
   * not by any single domain's version — see .claude/rules/changelog.md.
   */
  versions?: DomainVersion[]
  blocks: ChangelogBlock[]
}

// `Версии: Релей 0.22.0 · Веб 0.36.4` — sits directly under the `## DATE`
// heading. Each `·`-separated token is a domain from the fixed vocabulary plus
// a semver. See .claude/rules/changelog.md for the format.
const VERSIONS_LINE_RE = /^Версии:\s*(.+)$/
const VERSION_TOKEN_RE =
  /^(Веб|Релей|Бэкенд|Агент)\s+(\d+\.\d+\.\d+(?:-[\w.]+)?)$/

/**
 * Minimal markdown parser tailored to CHANGELOG.md shape — not a general-purpose
 * md renderer. Accepts:
 *   ## YYYY-MM-DD            -> entry keyed by date
 *   Версии: <Домен> <ver>... -> optional first body line, parsed into versions
 *   ### Heading              -> subsection heading inside current entry
 *   - bullet                 -> list item (consecutive bullets collapse into one list)
 *   anything else            -> paragraph
 * The top-level `# Changelog` intro is stripped (everything before the first `## `).
 */
export function parseChangelog(raw: string): ChangelogEntry[] {
  const parts = raw.split(/^## /m)
  const entries: ChangelogEntry[] = []
  for (let i = 1; i < parts.length; i += 1) {
    const chunk = parts[i]
    const newlineIdx = chunk.indexOf('\n')
    // The heading is the entry date. An unrecognized heading keeps its raw text
    // as `date` so the caller still sees the entry (it won't pass an ISO check).
    const date = (newlineIdx === -1 ? chunk : chunk.slice(0, newlineIdx)).trim()
    const body = newlineIdx === -1 ? '' : chunk.slice(newlineIdx + 1)

    const { versions, rest } = extractVersions(body)
    const entry: ChangelogEntry = { date, blocks: parseBody(rest) }
    if (versions) entry.versions = versions
    entries.push(entry)
  }
  return entries
}

/**
 * Pulls the optional `Версии: ...` line off the head of an entry body. The line
 * must be the first non-empty line (blank lines above it are skipped). Returns
 * the parsed domain versions and the body with that line removed. When the line
 * is absent — or present but no token parses — `versions` is undefined and the
 * body is returned unchanged, so nothing is silently dropped.
 */
function extractVersions(body: string): {
  versions?: DomainVersion[]
  rest: string
} {
  const lines = body.split('\n')
  let idx = 0
  while (idx < lines.length && lines[idx].trim() === '') idx += 1
  if (idx >= lines.length) return { rest: body }

  const match = VERSIONS_LINE_RE.exec(lines[idx].trim())
  if (!match) return { rest: body }

  const versions: DomainVersion[] = []
  for (const token of match[1].split('·')) {
    const tokenMatch = VERSION_TOKEN_RE.exec(token.trim())
    if (tokenMatch) {
      versions.push({ domain: tokenMatch[1], version: tokenMatch[2] })
    }
  }
  if (versions.length === 0) return { rest: body }

  lines.splice(idx, 1)
  return { versions, rest: lines.join('\n') }
}

function parseBody(body: string): ChangelogBlock[] {
  const blocks: ChangelogBlock[] = []
  let currentList: string[] | null = null
  let currentPara: string[] | null = null

  const flush = () => {
    if (currentList) {
      blocks.push({ kind: 'list', items: currentList })
      currentList = null
    }
    if (currentPara) {
      blocks.push({ kind: 'para', text: currentPara.join(' ') })
      currentPara = null
    }
  }

  for (const rawLine of body.split('\n')) {
    const line = rawLine.trim()
    if (line === '') {
      flush()
      continue
    }
    if (line.startsWith('### ')) {
      flush()
      blocks.push({ kind: 'heading', text: line.slice(4).trim() })
      continue
    }
    if (line.startsWith('- ')) {
      if (currentPara) flush()
      if (!currentList) currentList = []
      currentList.push(line.slice(2).trim())
      continue
    }
    if (currentList) flush()
    if (!currentPara) currentPara = []
    currentPara.push(line)
  }
  flush()
  return blocks
}

/**
 * Stable date-based anchor for both URL fragment and DOM id. Date is unique
 * across entries (one entry per date in the source CHANGELOG.md), so it stays
 * a stable anchor regardless of which domain versions the entry carries.
 */
export function entryAnchor(entry: ChangelogEntry): string {
  return `d-${entry.date}`
}

const MONTHS_RU = [
  'января',
  'февраля',
  'марта',
  'апреля',
  'мая',
  'июня',
  'июля',
  'августа',
  'сентября',
  'октября',
  'ноября',
  'декабря',
]

/**
 * ISO date (YYYY-MM-DD) → "5 мая 2026". Returns iso unchanged on parse failure.
 *
 * Named `*Ru` to disambiguate from `formatDate` in `lib/format.ts` which uses
 * `en-GB` ("15 Apr 2026"). Co-importing both is rare but the suffix makes the
 * locale obvious at the call site.
 */
export function formatDateRu(iso: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso)
  if (!match) return iso
  const year = Number(match[1])
  const month = Number(match[2])
  const day = Number(match[3])
  if (month < 1 || month > 12) return iso
  return `${day} ${MONTHS_RU[month - 1]} ${year}`
}

/** Short version: "5 мая". Used in compact TOC labels. */
export function formatDateRuShort(iso: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso)
  if (!match) return iso
  const month = Number(match[2])
  const day = Number(match[3])
  if (month < 1 || month > 12) return iso
  return `${day} ${MONTHS_RU[month - 1]}`
}
